import { captureException } from '@sentry/react'
import type { Middleware } from '@reduxjs/toolkit'
import { CROSS_TAB_LOGOUT_REASONS } from '../../app/syncPredicate'
import { LOGOUT } from './AuthConstants'

type LogoutAction = {
  type: typeof LOGOUT
  reason?: string
}

/**
 * LOGOUTs dispatched purely to reset the previous provider's cached state, each followed at
 * once by the new provider's LOGIN_SUCCESS. Tearing down storage there would purge the
 * session being established, and for a session-ending reason also reload to `/`:
 *   - the dev-tools persona switch;
 *   - `AuthValidate`'s session replacement. Its earlier `clearSessionLocally()` already
 *     purged this tab's storage, which the other tabs share, and the LOGOUT still resets
 *     the reducers everywhere. It stays in `CROSS_TAB_LOGOUT_REASONS` so the pair still
 *     reaches the other tabs together.
 */
const NON_SESSION_ENDING_REASONS = ['devtools-persona-switch', 'session-replaced']

function isLogoutAction(action: unknown): action is LogoutAction {
  return typeof action === 'object' && action !== null && 'type' in action && action.type === LOGOUT
}

/**
 * Clears browser storage on every LOGOUT, whatever dispatched it.
 *
 * Provider funnels manual sign-out, the idle timeout, the mount inactivity check,
 * `refreshSession`'s rejection and both restclient 401 paths through the one
 * `LOGOUT` action, so this is the single place that covers all of them — including the
 * copy `redux-state-sync` replays into the other tabs. Wiring each call site separately
 * would leave the next one to be added uncovered.
 *
 * How far the teardown goes depends on the reason. `CROSS_TAB_LOGOUT_REASONS`, less the
 * `NON_SESSION_ENDING_REASONS` above, end the session in every tab, so those also shred the
 * encryption key and reload to login. The rest stay local by design (QMD-4088: a background tab's network
 * blip must not tear down the tab holding a live visit) — they still purge, but they
 * neither navigate nor shred, because a surviving tab is still legitimately signed in.
 *
 * Runs after `next(action)` so the reducers have already reset: the flush inside the
 * teardown then writes post-logout state rather than re-persisting the roster.
 */
export const logoutTeardownMiddleware: Middleware = () => (next) => (action) => {
  const result = next(action)

  if (isLogoutAction(action) && !NON_SESSION_ENDING_REASONS.includes(action.reason ?? '')) {
    const endsSession = CROSS_TAB_LOGOUT_REASONS.includes(action.reason ?? '')
    // Imported lazily: this module is part of the store's own graph, and
    // `clearAllClientState` imports the persistor back out of it. The dynamic edge breaks
    // that cycle, and keeps `@segment/analytics-next` out of the entry bundle.
    // A chunk that fails to load (offline, blocked CDN, a stale deploy's hashed filename)
    // rejects here. Without this handler the teardown silently does not happen *and* the
    // rejection goes unobserved, so logout leaves persisted PHI on disk with nothing in
    // Sentry to say so. There is no fallback worth attempting — the persistor lives
    // behind this very import — so the honest degradation is to report it loudly.
    void import('../../utils/clearAllClientState')
      .then(({ clearAllClientState }) => clearAllClientState({ navigate: endsSession, shredKey: endsSession }))
      .catch((error: unknown) => {
        console.error('logoutTeardownMiddleware: failed to run the logout teardown', error)
        captureException(error)
      })
  }

  return result
}
